import { fillText } from 'components/invoiceImport/invoiceImportText';

/** Fired once a mail send answered (JBWEB-1282): the list and the detail read themselves again. */
export const INVOICE_MAIL_SENT = 'invoice_mail_sent';

/**
 * Every text of the mail modal and its entry points, as the `invoiceSendMail`
 * space of fr.js holds them (a test keeps the two equal): the default read
 * when a language has no such text.
 */
export const INVOICE_SEND_MAIL_TEXTS = {
  title: 'Envoyer par mail',
  open: 'Envoyer par mail',
  close: 'Fermer',
  intro:
    'Les factures sont envoyées en PDF aux contacts de facturation de chaque entreprise.',
  scopesLabel: 'Factures à envoyer',
  scope: {
    selectionOne: 'Cette facture',
    selectionOneDetail: 'N°{number} · {company} · {amount}',
    selectionMany: 'Ces factures',
    selectionManyDetail: '{count} factures sélectionnées',
    company: 'Toutes les factures de {company}',
    group: 'Toutes les factures du groupe {group}',
    invoicesOne: '{count} facture',
    invoicesMany: '{count} factures',
    companiesOne: '{count} entreprise',
    companiesMany: '{count} entreprises',
  },
  recipients: {
    title: 'Destinataires · contacts de facturation de {company}',
    none: 'Aucun contact de facturation pour cette entreprise.',
    member: 'Membre',
    pendingMember: 'Invitation en attente',
    external: 'Externe',
    unnamed: 'Contact sans nom',
  },
  withoutCompany: 'Sans entreprise',
  notSendable: {
    title: 'Écartées, non envoyables :',
    item: 'N°{number} · {status}',
  },
  status: {
    error: 'Erreur',
    draft: 'Brouillon',
    pending: 'En attente',
    partiallyPaid: 'Partiellement payé',
    paid: 'Payé',
    cancelled: 'Annulée',
    notImported: 'Non importé',
  },
  loading: 'Chargement des factures et des contacts…',
  reload: 'Réessayer',
  cancel: 'Annuler',
  sendOne: 'Envoyer {count} facture',
  sendMany: 'Envoyer {count} factures',
  sending: 'Envoi en cours…',
  retrySend: 'Réessayer l’envoi',
  frozenNotice:
    'Le premier envoi a peut-être abouti : la sélection est figée. Réessayez l’envoi à l’identique ou fermez la fenêtre.',
  entry: {
    notSendable:
      'Seules les factures en attente, partiellement payées ou payées s’envoient par mail.',
    tooMany: 'Envoyer {max} factures au plus à la fois',
  },
  report: {
    sentOne: '{count} facture envoyée',
    sentMany: '{count} factures envoyées',
    failedOne: '{count} facture en échec',
    failedMany: '{count} factures en échec',
    skippedOne: '{count} facture sautée',
    skippedMany: '{count} factures sautées',
    nothingSent: 'Aucune facture envoyée',
    replayed: 'Cet envoi avait déjà été fait : rien n’a été renvoyé.',
    line: '{company} : {reason}',
    noRecipient: 'aucun destinataire coché',
    unknownCompany: 'Une entreprise',
  },
  companyErrors: {
    INVOICE_PDF_MISSING: 'le PDF d’une facture manque',
    APPENDIX_FILE_NOT_FOUND: 'une annexe est introuvable',
    ATTACHMENTS_TOO_LARGE: 'les pièces jointes dépassent 20 Mo',
    BILLING_MAIL_SEND_FAILED: 'le service de mails a refusé l’envoi',
    INVOICE_MAIL_FAILED: 'l’envoi a échoué',
    INVOICE_NOT_SENDABLE: {
      one: '{numbers} écartée, non envoyable',
      many: '{numbers} écartées, non envoyables',
    },
  },
  errors: {
    INVOICE_NUMBERS_INVALID:
      'Cette sélection ne peut pas être envoyée : de 1 à 200 factures à la fois.',
    SCOPE_INVALID: 'Ce choix de factures n’est pas reconnu.',
    SCOPE_UNAVAILABLE:
      'Ce choix n’est plus possible pour cette sélection : choisissez-en un autre.',
    PERIOD_MIXED: 'Les factures sélectionnées doivent être du même mois.',
    SCOPE_TOO_LARGE:
      'Trop de factures pour un seul envoi : 300 factures et 30 entreprises au plus.',
    RECIPIENTS_INVALID:
      'Un destinataire coché n’est plus un contact de facturation de son entreprise : fermez et rouvrez la fenêtre.',
    NOTHING_TO_SEND: 'Aucune facture envoyable n’a de destinataire coché.',
    REQUEST_ID_INVALID:
      'Cet envoi n’a pas pu être identifié : fermez et rouvrez la fenêtre.',
    INVOICE_NOT_FOUND: 'Une facture de la sélection est introuvable.',
    SEND_IN_PROGRESS:
      'Cet envoi est encore en cours : patientez, puis réessayez. Rien ne partira deux fois.',
    BILLING_MAIL_DISABLED: 'L’envoi de mails est coupé sur cet environnement.',
    BILLING_MAIL_TEMPLATE_MISSING:
      'Le modèle du mail de facture manque sur cet environnement.',
    prepareFailed: 'Les factures et les contacts n’ont pas pu être chargés.',
    generic: 'L’envoi a échoué, réessayez.',
    network:
      'Le serveur n’a pas répondu. Réessayez : un envoi déjà parti ne partira pas deux fois.',
  },
};

const REQUEST_ERROR_CODES = Object.keys(INVOICE_SEND_MAIL_TEXTS.errors).filter(
  (key) => /^[A-Z_]+$/.test(key),
);

// The api's status → the key of its wording.
const STATUS_KEYS = {
  error: 'error',
  draft: 'draft',
  pending: 'pending',
  partially_paid: 'partiallyPaid',
  paid: 'paid',
  cancelled: 'cancelled',
  not_imported: 'notImported',
};

const defaultTextAt = (path) =>
  path
    .split('.')
    .reduce((value, key) => value?.[key], INVOICE_SEND_MAIL_TEXTS) ?? '';

/** `text("scope.company")`: the text of the language, else the French default. */
export const invoiceSendMailText = (language) => (path) =>
  language.getValue(`invoiceSendMail.${path}`, defaultTextAt(path));

/** The "one" or "many" variant, filled: French says « 0 facture ». */
export const countText = (text, path, count, values = {}) =>
  fillText(text(`${path}${count > 1 ? 'Many' : 'One'}`), { count, ...values });

export const statusText = (text, status) =>
  STATUS_KEYS[status] ? text(`status.${STATUS_KEYS[status]}`) : '';

/** « Prénom NOM », as the mockups write a contact. */
export const contactName = (text, contact) => {
  const name = [
    contact.firstname?.trim(),
    contact.lastname?.trim().toLocaleUpperCase('fr-FR'),
  ]
    .filter(Boolean)
    .join(' ');
  return name || text('recipients.unnamed');
};

/**
 * What the second line says of a contact: a member's role when the api gives
 * one (`roleLabel`), else that it is a member; an invitation not accepted
 * yet; a contact without an account.
 */
export const contactQualifier = (text, contact, roleLabel) => {
  if (contact.label === 'pending_member') {
    return text('recipients.pendingMember');
  }
  if (contact.label === 'external') return text('recipients.external');
  return roleLabel || text('recipients.member');
};

/**
 * An id the api accepts (16 to 64 of [A-Za-z0-9-]), drawn once per opening of
 * the modal: a retry after a lost answer carries it again, so the api answers
 * with what it already did instead of sending twice.
 */
export const createRequestId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (typeof cryptoApi?.randomUUID === 'function')
    return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

/**
 * What a refused request says. `fallbackKey` words a server failure or a
 * code the screen does not know; an answer that never came has its own text
 * when `networkKey` is given.
 */
export const requestFailureText = (
  text,
  error,
  { fallbackKey, networkKey },
) => {
  if (!error?.response) return text(`errors.${networkKey ?? fallbackKey}`);
  const code = error.response.data?.errorCode;
  if (REQUEST_ERROR_CODES.includes(code)) return text(`errors.${code}`);
  return text(`errors.${fallbackKey}`);
};

const numbersText = (numbers) =>
  numbers.map((number) => `N°${number}`).join(', ');

const reasonOf = (text, result) => {
  const numbers = result.invoiceNumbers ?? [];
  if (result.errorCode === 'INVOICE_NOT_SENDABLE') {
    return fillText(
      text(
        `companyErrors.INVOICE_NOT_SENDABLE.${numbers.length > 1 ? 'many' : 'one'}`,
      ),
      { numbers: numbersText(numbers) },
    );
  }
  if (result.status === 'skipped') return text('report.noRecipient');
  const known =
    result.errorCode &&
    result.errorCode !== 'INVOICE_NOT_SENDABLE' &&
    INVOICE_SEND_MAIL_TEXTS.companyErrors[result.errorCode];
  return text(
    `companyErrors.${known ? result.errorCode : 'INVOICE_MAIL_FAILED'}`,
  );
};

/**
 * The report of a send, from its `results`. A company may come twice (its
 * mail, then its invoices left out as not sendable) and the invoices without
 * a company come under `companyId: null`: everything is counted in invoices,
 * entry by entry, never in entries nor by company id alone.
 *
 * @param {object} params
 * @param {Function} params.text - from invoiceSendMailText
 * @param {object[]} params.results - the api's `results`
 * @param {boolean} params.replayed
 * @param {Map<string|null, string>} params.companyNames - id → name, from `prepare`
 * @returns {{ kind: "success"|"warning"|"error", lines: string[], sentNumbers: number[] }}
 */
export const buildMailReport = ({ text, results, replayed, companyNames }) => {
  const entries = Array.isArray(results) ? results : [];
  const invoicesOf = (entry) =>
    Array.isArray(entry.invoiceNumbers) ? entry.invoiceNumbers : [];
  const sent = entries.filter((entry) => entry.status === 'sent');
  const failed = entries.filter((entry) => entry.status === 'failed');
  const skipped = entries.filter((entry) => entry.status === 'skipped');
  const countOf = (list) =>
    list.reduce((total, entry) => total + invoicesOf(entry).length, 0);

  const sentCount = countOf(sent);
  const failedCount = countOf(failed);
  const skippedCount = countOf(skipped);

  const summary = [
    sentCount > 0
      ? countText(text, 'report.sent', sentCount)
      : text('report.nothingSent'),
  ];
  if (failedCount > 0)
    summary.push(countText(text, 'report.failed', failedCount));
  if (skippedCount > 0) {
    summary.push(countText(text, 'report.skipped', skippedCount));
  }

  // A company the modal did not show (the scope moved since `prepare`) is
  // still reported, without a name.
  const nameOf = (companyId) => {
    if (!companyId) return text('withoutCompany');
    return companyNames?.get(companyId) || text('report.unknownCompany');
  };
  const details = [...failed, ...skipped].map((entry) =>
    fillText(text('report.line'), {
      company: nameOf(entry.companyId ?? null),
      reason: reasonOf(text, entry),
    }),
  );

  let kind = 'success';
  if (failedCount > 0 || sentCount === 0)
    kind = sentCount > 0 ? 'warning' : 'error';

  return {
    kind,
    lines: [
      summary.join(' · '),
      ...details,
      ...(replayed ? [text('report.replayed')] : []),
    ],
    sentNumbers: sent.flatMap(invoicesOf),
  };
};
